import Hls from "hls.js";
import {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import playBigIcon from "../../assets/playbig.svg";
import "./VideoWithHls.css";
import { OrbitProgress } from "react-loading-indicators";

const VideoWithHLS = forwardRef(
  (
    {
      hlsUrl,
      onTimeUpdate,
      onDurationChange,
      isWideMode,
      markerPoint,
      isLocationMode = false,
      onLocationClick,
      resetMarkerPoint,
      isVideoWaiting,
      setIsVideoWaiting,
      setPlaybackStarted,
    },

    ref
  ) => {
    const videoRef = useRef(null);
    const hlsRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [volume, setVolume] = useState(1);

    const handleTimeUpdate = useCallback(() => {
      if (videoRef.current) {
        onTimeUpdate?.(videoRef.current.currentTime);
      }
    }, [onTimeUpdate]);

    const handleDurationChange = useCallback(() => {
      if (videoRef.current) {
        onDurationChange?.(videoRef.current.duration);
      }
    }, [onDurationChange]);

    const handleFullscreenChange = useCallback(() => {
      const isFullscreenNow = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isFullscreenNow);
    }, []);

    const toggleFullscreen = useCallback(async () => {
      if (!videoRef.current) return;

      try {
        if (!isFullscreen) {
          if (videoRef.current.requestFullscreen) {
            await videoRef.current.requestFullscreen();
          } else if (videoRef.current.webkitRequestFullscreen) {
            await videoRef.current.webkitRequestFullscreen();
          } else if (videoRef.current.webkitRequestFullScreen) {
            await videoRef.current.webkitRequestFullScreen();
          } else if (videoRef.current.webkitEnterFullScreen) {
            await videoRef.current.webkitEnterFullScreen();
          } else if (videoRef.current.mozRequestFullScreen) {
            await videoRef.current.mozRequestFullScreen();
          } else if (videoRef.current.msRequestFullscreen) {
            await videoRef.current.msRequestFullscreen();
          }
        } else {
          if (document.exitFullscreen) {
            await document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
            await document.webkitExitFullscreen();
          } else if (document.mozCancelFullScreen) {
            await document.mozCancelFullScreen();
          } else if (document.msExitFullscreen) {
            await document.msExitFullscreen();
          }
        }
      } catch (error) {
        console.error("Fullscreen error:", error);
      }
    }, [isFullscreen]);

    const handlePlay = async () => {
      resetMarkerPoint();
      console.log("Play requested");
      setPlaybackStarted(true);
      try {
        if (videoRef.current) {
          const playPromise = videoRef.current.play();
          if (playPromise !== undefined) {
            await playPromise;
            console.log("Play successful");
            setIsPlaying(true);
          }
        }
      } catch (error) {
        console.error("Play failed:", error);
        setIsPlaying(false);
      }
    };

    const handlePause = () => {
      resetMarkerPoint();
      console.log("Pause requested");
      if (videoRef.current) {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    };

    const handleVideoClick = () => {
      console.log("Video clicked, current state:", isPlaying);

      if (isPlaying) {
        handlePause();
      } else {
        handlePlay();
      }
    };

    const toggleWide = useCallback(() => {
      return !isWideMode;
    }, [isWideMode]);

    // Add volume change handler
    const handleVolumeChange = useCallback(() => {
      if (videoRef.current) {
        setVolume(videoRef.current.volume);
      }
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        controls: {
          play: handlePlay,
          pause: handlePause,
          seek: (time) => {
            if (videoRef.current) {
              const targetTime = Math.max(
                0,
                Math.min(time, videoRef.current.duration || 0)
              );
              videoRef.current.currentTime = targetTime;
              onTimeUpdate?.(targetTime);
            }
          },
          getCurrentTime: () => videoRef.current?.currentTime || 0,
          getDuration: () => videoRef.current?.duration || 0,
          isPlaying,
          toggleFullscreen,
          isFullscreen,
          toggleWide,
          isWideMode,
          setVolume: (newVolume) => {
            if (videoRef.current) {
              videoRef.current.volume = newVolume;
              setVolume(newVolume);
            }
          },
          getVolume: () => volume,
          setPlaybackRate: (rate) => {
            if (videoRef.current) {
              videoRef.current.playbackRate = rate;
            }
          },
          getVideoElement: () => videoRef.current,
        },
      }),
      [
        isPlaying,
        onTimeUpdate,
        toggleFullscreen,
        isFullscreen,
        toggleWide,
        isWideMode,
        volume,
      ]
    );

    // Initialize HLS
    useEffect(() => {
      const video = videoRef.current;
      if (!video || !hlsUrl || !Hls.isSupported()) return;

      // Only create new HLS instance if we don't have one
      if (!hlsRef.current) {
        console.log("Initializing HLS");
        const hls = new Hls({
          debug: false,
          enableWorker: true,
          startPosition: -1,
          maxBufferLength: 30,
          maxMaxBufferLength: 600,
          maxBufferSize: 60 * 1000 * 1000,
        });
        hlsRef.current = hls;

        hls.on(Hls.Events.ERROR, (event, data) => {
          console.error("HLS Error:", data);
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.log("Network error, trying to recover");
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.log("Media error, trying to recover");
                hls.recoverMediaError();
                break;
              default:
                console.log("Fatal error, destroying");
                hls.destroy();
                break;
            }
          }
        });

        hls.attachMedia(video);
      }

      const hls = hlsRef.current;

      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        console.log("HLS Media attached");
        hls.loadSource(hlsUrl);
      });

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log("HLS Manifest parsed");
      });

      return () => {
        // Only destroy HLS if URL changes
        if (hlsRef.current) {
          console.log("Cleaning up HLS");
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
      };
    }, [hlsUrl]); // Only reinitialize if URL changes

    // Handle video events
    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;

      const handlePlayEvent = () => {
        console.log("Play event fired");
        setIsPlaying(true);
      };

      const handlePauseEvent = () => {
        console.log("Pause event fired");
        setIsPlaying(false);
      };

      const handleEnded = () => {
        console.log("Video ended");
        setIsPlaying(false);
        video.currentTime = 0;
        onTimeUpdate?.(0);
      };

      const handleCanPlay = () => {
        console.log("Video can play");
        setIsVideoWaiting(false);
      };

      const handleWaiting = () => {
        console.log("Video is waiting");
        setIsVideoWaiting(true);
      };

      video.addEventListener("timeupdate", handleTimeUpdate);
      video.addEventListener("loadedmetadata", handleDurationChange);
      video.addEventListener("play", handlePlayEvent);
      video.addEventListener("pause", handlePauseEvent);
      video.addEventListener("ended", handleEnded);
      video.addEventListener("canplay", handleCanPlay);
      video.addEventListener("waiting", handleWaiting);

      // Add fullscreen change event listeners
      document.addEventListener("fullscreenchange", handleFullscreenChange);
      document.addEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.addEventListener("mozfullscreenchange", handleFullscreenChange);
      document.addEventListener("MSFullscreenChange", handleFullscreenChange);

      video.addEventListener("volumechange", handleVolumeChange);

      return () => {
        video.removeEventListener("timeupdate", handleTimeUpdate);
        video.removeEventListener("loadedmetadata", handleDurationChange);
        video.removeEventListener("play", handlePlayEvent);
        video.removeEventListener("pause", handlePauseEvent);
        video.removeEventListener("ended", handleEnded);
        video.removeEventListener("canplay", handleCanPlay);
        video.removeEventListener("waiting", handleWaiting);

        // Remove fullscreen change event listeners
        document.removeEventListener(
          "fullscreenchange",
          handleFullscreenChange
        );
        document.removeEventListener(
          "webkitfullscreenchange",
          handleFullscreenChange
        );
        document.removeEventListener(
          "mozfullscreenchange",
          handleFullscreenChange
        );
        document.removeEventListener(
          "MSFullscreenChange",
          handleFullscreenChange
        );
        video.removeEventListener("volumechange", handleVolumeChange);
      };
    }, [
      handleTimeUpdate,
      handleDurationChange,
      onTimeUpdate,
      handleFullscreenChange,
      handleVolumeChange,
    ]);

    return (
      <div
        className="video-container"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          position: "relative",
          backgroundColor: "transparent",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <video
          ref={videoRef}
          controls={false}
          className="video-component"
          preload="auto"
          style={{
            width: "100%",
            height: "100%",
            cursor: isLocationMode ? "crosshair" : "pointer",
            objectFit: "contain",
            maxWidth: isWideMode ? "1600px" : "100%",
            margin: isWideMode ? "0 auto" : "0",
          }}
          onClick={handleVideoClick}
          playsInline
        />
        {/* Overlay for location mode */}
        {isLocationMode && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              cursor: "crosshair",
              pointerEvents: "auto",
              zIndex: 3000,
              background: "rgba(0,0,0,0)",
            }}
            onClick={onLocationClick}
          />
        )}
        {/* {console.log("VideoWithHLS markerPoint:", markerPoint)} */}
        {markerPoint && (
          <div
            style={{
              position: "absolute",
              left: `${markerPoint.x}%`,
              top: `${markerPoint.y}%`,
              transform: "translate(-50%, -50%)",
              width: "24px",
              height: "24px",
              opacity: 0.5,
              backgroundColor: "#3496FF",
              borderRadius: "50%",
              pointerEvents: "none",
              zIndex: 2000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
              fontSize: "12px",
              color: "black",
            }}
          >
            <div
              style={{
                width: "10px",
                height: "10px",
                backgroundColor: "blue",
                borderRadius: "5px",
              }}
            />
          </div>
        )}
        {(isHovered || !isPlaying) && (
          <div className="video-overlay">
            {!isPlaying && (
              <img
                src={playBigIcon}
                alt="play"
                className="play-big-icon"
                onClick={handleVideoClick}
              />
            )}
          </div>
        )}
        {isVideoWaiting && (
          <div className="video-overlay">
            <OrbitProgress color="white" style={{ fontSize: 20 }} />
          </div>
        )}
      </div>
    );
  }
);

export default VideoWithHLS;
